import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  EngagementStatus,
  IEngagement
} from "@wingspanhq/payments/dist/interfaces";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import React from "react";
import { ErrorContextKey } from "../../../services/platform";
import { ActionEngagementDetails } from "../components/ActionEngagementDetails";
import { useUpdateEngagement } from "../../../query/engagements/mutations/useUpdateEngagement";

export interface ArchiveEngagementTypeModalProps {
  engagement: IEngagement;
  onClose: () => void;
}

export const ArchiveEngagementTypeModal: React.FC<
  ArchiveEngagementTypeModalProps
> = ({ engagement, onClose }) => {
  const { openSnackbar } = useWSSnackbar();
  const [updateEngagementType, updateEngagementTypeMeta] =
    useUpdateEngagement();

  const handleArchive = async () => {
    await updateEngagementType({
      engagementId: engagement.engagementId,
      status: EngagementStatus.Inactive
    });

    openSnackbar({
      theme: "dark",
      type: "success",
      message: "Engagement type archived"
    });
    onClose();
  };

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to archive this engagement type?
      </WSText.ParagraphSm>
      <ActionEngagementDetails engagement={engagement} />

      <WSAlert
        my="2XL"
        theme="info"
        icon="info-circle"
        size="M"
        title="Archiving engagement type - what to expect:"
      >
        <WSElement as="ul" ml="L">
          <li>
            Any archived contractor engagements associated with this engagement
            type will remain archived and cannot be restored while the
            engagement type remains archived.
          </li>
          <li>No changes can be made to this engagement type.</li>
          <li>
            No additional invoices can be created for this engagement type.
          </li>
          <li>
            Incomplete contractor requirements exclusively associated with this
            engagement type will also be archived.
          </li>
        </WSElement>
        Existing invoices will remain on the Invoicing page until you delete
        them. You can always restore the engagement type when needed.
      </WSAlert>

      <WSErrorMessage
        error={updateEngagementTypeMeta.error}
        mb="XL"
        contextKey={ErrorContextKey.Engagement}
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Archive engagement type",
            loading: updateEngagementTypeMeta.isLoading,
            onAsyncClick: handleArchive,
            kind: "Primary"
          }
        ]}
      />
    </>
  );
};

export const useArchiveEngagementTypeModal = () => {
  return useWSModal(ArchiveEngagementTypeModal, {
    title: "Archive engagement type",
    size: "M"
  });
};
